"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Avatar } from "@/components/ui/avatar"
import { Loader2, MessageCircle, Send } from "lucide-react"
import { fetchComments, submitComment } from "@/lib/notion-client"
import type { Comment } from "@/lib/types"
import { formatDistanceToNow } from "date-fns"
import { useWordPressAuth } from "@/components/wordpress-auth-provider"
import { UserAvatar } from "@/components/user-avatar"

interface CommentsSectionProps {
  id: string
  type: "task" | "project"
  title?: string
}

export function CommentsSection({ id, type, title }: CommentsSectionProps) {
  const { user } = useWordPressAuth()
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load comments on mount
  useEffect(() => {
    loadComments()
  }, [id, type])

  const loadComments = async (force = false) => {
    try {
      setIsLoading(true)
      setError(null)
      const fetchedComments = await fetchComments(id, type, force)
      setComments(fetchedComments)
    } catch (err) {
      console.error('Failed to load comments:', err)
      setError(err instanceof Error ? err.message : 'Failed to load comments')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitComment = async () => {
    if (!newComment.trim() || isSubmitting) return

    try {
      setIsSubmitting(true)
      setError(null)
      
      await submitComment(id, type, newComment.trim())
      setNewComment("")
      
      // Reload comments to show the new one
      await loadComments(true)
    } catch (err) {
      console.error('Failed to submit comment:', err)
      setError(err instanceof Error ? err.message : 'Failed to submit comment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSubmitComment()
    }
  }

  const formatCommentDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr)
      return formatDistanceToNow(date, { addSuffix: true })
    } catch {
      return dateStr
    }
  }

  const getAuthorInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Enhance comment with current user data if email matches
  const enhanceCommentWithUserData = (comment: Comment): Comment => {
    if (!user || !user.email || comment.author.email !== user.email) {
      return comment
    }

    // If the comment author email matches the current user's email,
    // use the current user's data for display
    return {
      ...comment,
      author: {
        ...comment.author,
        name: user.displayName,
        email: user.email,
        avatar: user.avatarUrl || comment.author.avatar, // Use WordPress avatar or fallback to original
      }
    }
  }

  // Process comments to enhance with user data
  const enhancedComments = comments.map(enhanceCommentWithUserData)

  if (isLoading) {
    return (
      <Card className="mt-6">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            <h3 className="font-semibold">Comments</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading comments...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="mt-6">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            <h3 className="font-semibold">
              Comments {enhancedComments.length > 0 && `(${enhancedComments.length})`}
            </h3>
          </div>
          {title && (
            <span className="text-sm text-muted-foreground">
              on {title}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
            <p className="text-sm text-destructive">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => loadComments(true)}
            >
              Try again
            </Button>
          </div>
        )}

        {/* Add new comment */}
        <div className="space-y-3">
          <Textarea
            placeholder={`Add a comment to this ${type}...`}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={handleKeyPress}
            className="min-h-[80px] resize-none"
            disabled={isSubmitting}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Press Ctrl/Cmd + Enter to submit
            </p>
            <Button
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || isSubmitting}
              size="sm"
              className="gap-2"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isSubmitting ? "Submitting..." : "Comment"}
            </Button>
          </div>
        </div>

        {/* Comments list */}
        {enhancedComments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No comments yet</p>
            <p className="text-sm mt-1">Be the first to add a comment!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {enhancedComments.map((comment) => {
              const isCurrentUser = user?.email && comment.author.email === user.email
              
              return (
                <div key={comment.id} className="flex gap-3 p-4 bg-muted/30 rounded-lg">
                  {isCurrentUser ? (
                    <UserAvatar 
                      className="h-8 w-8"
                      displayName={comment.author.name}
                      alt={comment.author.name}
                    />
                  ) : (
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      {comment.author.avatar ? (
                        <img 
                          src={comment.author.avatar} 
                          alt={comment.author.name}
                          className="h-full w-full object-cover" 
                        />
                      ) : (
                        <div className="h-full w-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs font-medium text-primary">
                            {getAuthorInitials(comment.author.name)}
                          </span>
                        </div>
                      )}
                    </Avatar>
                  )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{comment.author.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatCommentDate(comment.createdAt)}
                    </span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {comment.content}
                  </div>
                  {comment.attachments && comment.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {comment.attachments.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline"
                        >
                          Attachment {index + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )
            })}
          </div>
        )}

        {/* Refresh button */}
        {comments.length > 0 && (
          <div className="pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadComments(true)}
              disabled={isLoading}
              className="gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              Refresh comments
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}